import type { ReactNode } from 'react';
import { makeStyles } from '@material-ui/core/styles';

/*
 * Skin for the org plugin's Ownership card.
 *
 * `PluginOrgComponentsGrid` paints each count tile with
 * `theme.getPageTheme({ themeId: type }).backgroundImage` — Backstage's per-kind
 * GRADIENT — and white type on top. That is a dark-theme artefact: on the white
 * canvas the group page renders three saturated gradient slabs that belong to no
 * tier in the elevation ladder. The gradient comes from the MUI theme's page
 * themes, so it cannot be turned off through a token; the tiles are re-skinned
 * here instead, to the same flat-surface + hairline grammar as every other card.
 *
 * The wrapper is `display: contents`, so it adds no box to the card stack, and
 * every rule is a descendant selector on the vendor's own stable class prefixes
 * (`makeStyles({ name: 'PluginOrg…' })` emits `PluginOrgComponentsGrid-card-<n>`,
 * hence the `[class*=]` prefix match). Two classes beats the vendor's one, so no
 * rule here needs `!important`.
 */
const useStyles = makeStyles({
  root: {
    display: 'contents',

    /*
     * The vendor lays the tiles out with `Grid item xs={6} lg={4}` and no
     * spacing, so they butt together and stretch to a third of the card at any
     * width. An auto-fill track keeps them at a stat-tile size and carries the
     * 10px gutter.
     */
    '& [class*="PluginOrgOwnershipCard-grid"]': {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(146px, 1fr))',
      gap: 10,
      width: '100%',
      margin: 0,
      /*
       * The vendor sets `overflowY: auto` on this grid, which clips each tile's
       * ring — the `0 0 0 1px` hairline sits OUTSIDE the border box, and
       * `overflow-y` forces `overflow-x` away from `visible` too, so the tiles
       * lost their right and bottom edges. Nothing here needs to scroll; the
       * card grows to its content.
       */
      overflow: 'visible',
    },
    '& [class*="PluginOrgOwnershipCard-grid"] > [class*="MuiGrid-item"]': {
      display: 'flex',
      maxWidth: 'none',
      flexBasis: 'auto',
      padding: 0,
    },

    /* Each tile is wrapped in a `Link`, which is the element that has to stretch. */
    '& [class*="PluginOrgOwnershipCard-grid"] > [class*="MuiGrid-item"] > a': {
      display: 'block',
      width: '100%',
      textDecoration: 'none',
    },
    '& [class*="PluginOrgOwnershipCard-grid"] > [class*="MuiGrid-item"] > a:hover':
      {
        textDecoration: 'none',
      },

    '& [class*="PluginOrgComponentsGrid-card"]': {
      background: 'var(--bui-white)',
      backgroundImage: 'none',
      color: 'var(--bui-fg-primary)',
      border: 'none',
      borderRadius: 12,
      boxShadow: 'var(--hone-portal-elevation-raised)',
      padding: '12px 14px',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      textAlign: 'left',
      minHeight: 88,
      boxSizing: 'border-box',
      gap: 2,
      transition: 'box-shadow 120ms ease',
    },
    '& [class*="PluginOrgComponentsGrid-card"]:hover': {
      boxShadow: 'var(--hone-portal-ring-hairline), 0 4px 10px rgba(0, 0, 0, 0.06)',
    },

    /* The count: the mono numeral the scorecards and stat strips already use. */
    '& [class*="PluginOrgComponentsGrid-card"] > [class*="MuiTypography-h6"]': {
      fontFamily: 'var(--bui-font-monospace)',
      fontSize: 22,
      fontWeight: 600,
      lineHeight: 1.15,
      color: 'var(--bui-fg-primary)',
    },

    /*
     * The kind/type label. The vendor centres it through a `sx` prop, which MUI
     * v4's `Box` ignores, so only the tile's own `alignItems` was holding it —
     * left-aligning the tile is enough to left-align the label with it.
     */
    '& [class*="PluginOrgComponentsGrid-card"] [class*="MuiBox-root"]': {
      width: '100%',
      textAlign: 'left',
    },
    /*
     * Case. The label is uppercased in the DOM, not by CSS — the vendor calls
     * `pluralize(rawTitle.toLocaleUpperCase('en-US'), counter)`, so "Repository"
     * arrives as "REPOSITORIES" and `text-transform: none` cannot undo it. The
     * mock has no uppercase anywhere, so the string is lowercased and its first
     * letter re-capitalised. These are single words ("Repositories", "Library",
     * "System"), so a `::first-letter` is enough; a multi-word type would come
     * back sentence case rather than title case, which is still the mock's
     * grammar.
     */
    '& [class*="PluginOrgComponentsGrid-card"] [class*="MuiBox-root"] [class*="MuiTypography-h6"]':
      {
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: '0.03em',
        color: 'var(--bui-fg-secondary)',
        textAlign: 'left',
        textTransform: 'lowercase',
      },
    '& [class*="PluginOrgComponentsGrid-card"] [class*="MuiBox-root"] [class*="MuiTypography-h6"]::first-letter':
      {
        textTransform: 'uppercase',
      },

    /* Present only on typed tiles ("Component" under "1 LIBRARY"). */
    '& [class*="PluginOrgComponentsGrid-card"] [class*="MuiTypography-subtitle1"]':
      {
        fontSize: 11,
        lineHeight: 1.4,
        color: 'var(--hone-portal-fg-muted)',
      },
  },
});

export function OwnershipTiles({ children }: { children: ReactNode }) {
  const classes = useStyles();
  return <div className={classes.root}>{children}</div>;
}
